import type { Signal } from '../../../shared/types';
import { NO_CITY_LABEL, SIGNAL_KIND_LABELS } from '../ui/labels';
import { SOURCE_NAMES, signalEventLabel } from './signals';

/** The New businesses filters (D66): where it came from, what happened, the category and the city.
 *  Nothing ticked in a filter lets all of it through; ticks in one filter are any-of, filters are all-of. */
export const FILTER_KEYS = ['source', 'type', 'kind', 'city'] as const;
export type FilterKey = (typeof FILTER_KEYS)[number];
export type Filters = Record<FilterKey, Set<string>>;

export const emptyFilters = (): Filters => ({
  source: new Set(),
  type: new Set(),
  kind: new Set(),
  city: new Set(),
});

/** "SANTA ANA " and "Santa Ana" are one city; no city is "". */
const cityName = (city: string | null): string =>
  (city ?? '')
    .trim()
    .toLowerCase()
    .replace(/(^|[\s-])\S/g, (c) => c.toUpperCase());

export const filterValue = (key: FilterKey, s: Signal): string => {
  if (key === 'source') return s.source;
  if (key === 'type') return signalEventLabel(s);
  if (key === 'kind') return s.kind;
  return cityName(s.city);
};

export const filterLabel = (key: FilterKey, value: string): string => {
  if (key === 'source') return SOURCE_NAMES[value as Signal['source']] ?? value;
  if (key === 'kind') return SIGNAL_KIND_LABELS[value as Signal['kind']] ?? value;
  if (key === 'city') return value || NO_CITY_LABEL;
  return value;
};

/** Whether a signal passes every filter, or every one but `except` (to count that filter's choices). */
export const matchesFilters = (f: Filters, s: Signal, except?: FilterKey): boolean =>
  FILTER_KEYS.every((k) => k === except || f[k].size === 0 || f[k].has(filterValue(k, s)));

export interface FilterOption {
  value: string;
  label: string;
  count: number;
}

/** A filter's choices: every value in the list, each counted under the other filters. The order never
 *  moves while ticking: cities by name (no city last), the rest by how many the whole list has. A
 *  ticked value whose businesses have all been triaged stays, last, at 0, so it can be unticked. */
export const filterOptions = (all: readonly Signal[], f: Filters, key: FilterKey): FilterOption[] => {
  const totals = new Map<string, number>();
  const counts = new Map<string, number>();
  for (const s of all) {
    const v = filterValue(key, s);
    totals.set(v, (totals.get(v) ?? 0) + 1);
    if (matchesFilters(f, s, key)) counts.set(v, (counts.get(v) ?? 0) + 1);
  }
  const byName = (a: string, b: string) =>
    key === 'city' && (a === '' || b === '')
      ? Number(a === '') - Number(b === '')
      : filterLabel(key, a).localeCompare(filterLabel(key, b));
  const gone = [...f[key]].filter((v) => !totals.has(v));
  return [
    ...[...totals.keys()].sort((a, b) =>
      key === 'city' ? byName(a, b) : totals.get(b)! - totals.get(a)! || byName(a, b),
    ),
    ...gone,
  ].map((value) => ({ value, label: filterLabel(key, value), count: counts.get(value) ?? 0 }));
};

/** What is ticked, filter by filter, for the removable tags above the list. */
export const activeFilters = (f: Filters): { key: FilterKey; value: string; label: string }[] =>
  FILTER_KEYS.flatMap((key) => [...f[key]].map((value) => ({ key, value, label: filterLabel(key, value) })));
